import { CompletionContext, CompletionResult } from '@codemirror/autocomplete'
import { latexAPI } from '@/api/latex'
import { logError, logDebug } from '@utils/logger'

// Cache for image file list
const imageCache: Map<string, { images: string[]; timestamp: number }> = new Map()
const CACHE_TTL = 60000 // 60 seconds (images change less frequently than citations)

/**
 * Image autocomplete provider for LaTeX \includegraphics{} commands
 * Detects \includegraphics{ pattern and provides suggestions from project images
 */
export function imageAutocompletion(documentId: string | null | undefined) {
  return async (context: CompletionContext): Promise<CompletionResult | null> => {
    // Only work in LaTeX mode
    if (!documentId) {
      logDebug('[imageAutocomplete] No documentId, skipping')
      return null
    }
    
    const { state, pos } = context
    const text = state.doc.toString()
    
    logDebug('[imageAutocomplete] Called', { pos, textBefore: text.substring(Math.max(0, pos - 50), pos) })
    
    // Look backwards from cursor to find \includegraphics{ pattern
    // Pattern: \includegraphics[...]{...} where we're inside the braces
    // Must handle: \includegraphics{ and \includegraphics[width=0.8\textwidth]{
    let start = pos - 1
    let depth = 0
    let foundIncludeGraphics = false
    let pathStart = pos
    
    // Scan backwards to find the opening brace of \includegraphics{
    while (start >= 0 && (pos - start) <= 200) {
      const char = text[start]
      
      if (char === '}') {
        depth++
      } else if (char === '{') {
        if (depth === 0) {
          // Found opening brace - check if preceded by \includegraphics
          // Look back enough to catch \includegraphics[width=0.8\textwidth]{
          const beforeBrace = text.substring(Math.max(0, start - 50), start)
          
          // Check if it contains \includegraphics before the brace
          // This handles cases like: 
          // - \includegraphics{
          // - \includegraphics[width=0.8\textwidth]{
          // - \includegraphics [width=0.5\textwidth] {
          const trimmed = beforeBrace.trim()
          
            // Find the position of \includegraphics
            const includeGraphicsPos = trimmed.lastIndexOf('\\includegraphics')
            if (includeGraphicsPos >= 0) {
              // Get everything after \includegraphics
              const afterCommand = trimmed.substring(includeGraphicsPos + '\\includegraphics'.length)
              
              // After \includegraphics, we should have:
              // 1. Nothing (just whitespace) - \includegraphics{
              // 2. [options] - \includegraphics[width=0.8\textwidth]{
              // 3. [options] + whitespace - \includegraphics[width=0.8\textwidth] {
              // 
              // The pattern should match: optional [anything] followed by optional whitespace, then the brace
              // We need to handle options like [width=0.8\textwidth] which contain backslashes and text
              
              // Simple approach: check if afterCommand matches the pattern:
              // - Empty or whitespace only: valid
              // - Starts with [ and ends with ] (with optional whitespace): valid
              // - Starts with [ and has ] somewhere, followed by optional whitespace: valid
              
              const afterTrimmed = afterCommand.trim()
              
              // Case 1: Empty or whitespace only - valid
              if (afterTrimmed === '') {
                foundIncludeGraphics = true
                pathStart = start + 1
                break
              }
              
              // Case 2: Starts with [ - check if it's a valid options block
              if (afterTrimmed.startsWith('[')) {
                // Find the matching closing bracket
                let bracketCount = 0
                let foundClosingBracket = false
                let i = 0
                
                while (i < afterTrimmed.length) {
                  if (afterTrimmed[i] === '[') {
                    bracketCount++
                  } else if (afterTrimmed[i] === ']') {
                    bracketCount--
                    if (bracketCount === 0) {
                      foundClosingBracket = true
                      i++
                      break
                    }
                  }
                  i++
                }
                
                // If we found a matching closing bracket, check if there's only whitespace after it
                if (foundClosingBracket) {
                  const afterBrackets = afterTrimmed.substring(i).trim()
                  if (afterBrackets === '') {
                    // Valid: [options] followed by optional whitespace
                    foundIncludeGraphics = true
                    pathStart = start + 1
                    break
                  }
                }
              }
            }
          
          // If we found a brace but it's not includegraphics, abort
          return null
        } else {
          depth--
        }
      }
      
      start--
    }
    
    if (!foundIncludeGraphics) {
      return null
    }
    
    // Extract the current query (text after {)
    const query = text.substring(pathStart, pos)
    
    logDebug('[imageAutocomplete] Detected \\includegraphics{ pattern', { query, pathStart, pos, textAround: text.substring(Math.max(0, pathStart - 30), Math.min(text.length, pos + 10)) })
    
    // Check cache first
    const cacheKey = documentId
    const cached = imageCache.get(cacheKey)
    const now = Date.now()
    
    let images: string[] = []
    
    if (cached && (now - cached.timestamp) < CACHE_TTL) {
      images = cached.images
      logDebug('[imageAutocomplete] Using cached images', { count: images.length })
    } else {
      try {
        // Fetch file tree from API
        const fileTree = await latexAPI.getFileTree(documentId)
        
        // Extract image files from the tree
        const extractImages = (files: any[]): string[] => {
          const imageFiles: string[] = []
          
          for (const file of files) {
            if (file.file_type === 'file') {
              // Check if it's an image file
              const ext = file.name.split('.').pop()?.toLowerCase() || ''
              const isImage = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'pdf', 'bmp', 'webp'].includes(ext)
              
              if (isImage) {
                // Use file_path (relative path) for autocomplete
                imageFiles.push(file.file_path)
              }
            }
            
            // Recursively check children
            if (file.children && Array.isArray(file.children)) {
              imageFiles.push(...extractImages(file.children))
            }
          }
          
          return imageFiles
        }
        
        images = extractImages(fileTree.files || [])
        
        // Cache the results
        imageCache.set(cacheKey, { images, timestamp: now })
        logDebug('[imageAutocomplete] Fetched images from API', { count: images.length })
      } catch (error) {
        logError('[imageAutocomplete] Failed to fetch images:', error)
        return null
      }
    }
    
    if (images.length === 0) {
      logDebug('[imageAutocomplete] No images found in project')
      return null
    }
    
    // Filter images based on query (pattern matching)
    // Show ALL images when query is empty, filter as user types
    let filteredImages = images
    const queryTrimmed = query.trim()
    
    if (queryTrimmed) {
      // User is typing - filter images based on query
      const queryLower = queryTrimmed.toLowerCase()
      filteredImages = images.filter(imagePath => {
        const imageName = imagePath.toLowerCase()
        // Match if query is contained in the path or filename
        // Also match if the path starts with the query (for directory paths like "images/")
        return imageName.includes(queryLower) || imageName.startsWith(queryLower)
      })
    }
    // If query is empty, show all images (filteredImages = images already, no filtering needed)
    
    // Always return suggestions if we're inside \includegraphics{}
    // If user typed something but no matches, still show all images so they can browse
    if (filteredImages.length === 0) {
      // No matches found - show all images anyway so user can see what's available
      filteredImages = images
    }
    
    // Build completion options
    const options = filteredImages.map((imagePath) => {
      // Extract filename for display
      const fileName = imagePath.split('/').pop() || imagePath
      const directory = imagePath.includes('/') 
        ? imagePath.substring(0, imagePath.lastIndexOf('/'))
        : ''
      
      // Show directory path in info for better context
      const displayInfo = directory ? `${directory}/${fileName}` : fileName
      
      return {
        label: imagePath, // Full path as label
        type: 'text' as const,
        info: displayInfo, // Show directory/filename for context
        detail: directory || undefined, // Show directory as detail if present
        apply: imagePath, // Insert the full path
      }
    })
    
    // Sort by relevance (exact matches first, then by directory match, then by length)
    const queryLower = queryTrimmed.toLowerCase()
    options.sort((a, b) => {
      const aLabel = a.label.toLowerCase()
      const bLabel = b.label.toLowerCase()
      
      // Priority 1: Exact prefix matches (starts with query)
      const aStartsWith = aLabel.startsWith(queryLower)
      const bStartsWith = bLabel.startsWith(queryLower)
      if (aStartsWith && !bStartsWith) return -1
      if (!aStartsWith && bStartsWith) return 1
      
      // Priority 2: If query is a directory path (ends with /), prioritize files in that directory
      if (queryTrimmed.endsWith('/')) {
        const aInDir = aLabel.startsWith(queryLower)
        const bInDir = bLabel.startsWith(queryLower)
        if (aInDir && !bInDir) return -1
        if (!aInDir && bInDir) return 1
      }
      
      // Priority 3: Filename matches (query in filename)
      const aFileName = aLabel.split('/').pop() || ''
      const bFileName = bLabel.split('/').pop() || ''
      const aFileNameMatch = aFileName.includes(queryLower)
      const bFileNameMatch = bFileName.includes(queryLower)
      if (aFileNameMatch && !bFileNameMatch) return -1
      if (!aFileNameMatch && bFileNameMatch) return 1
      
      // Priority 4: Shorter paths first (more likely to be root-level files)
      return a.label.length - b.label.length
    })
    
    logDebug('[imageAutocomplete] Returning completions', { 
      count: options.length, 
      from: pathStart, 
      to: pos,
      query: queryTrimmed,
      totalImages: images.length,
      filteredImages: filteredImages.length,
      sampleOptions: options.slice(0, 5).map(o => o.label)
    })
    
    // Return all options (don't limit) so user can scroll through all images
    // CodeMirror will handle rendering efficiently
    return {
      from: pathStart,
      to: pos,
      options: options, // Show all filtered options - user can scroll
      filter: false, // Don't filter - we already filtered based on query
      // Allow any characters except closing brace for image paths
      // This includes slashes, dots, letters, numbers, etc.
      span: /^[^}]*$/, // Valid characters for image paths (anything except closing brace)
      validFor: /^[^}]*$/, // Completion is valid while typing image path
    } as CompletionResult & { span: RegExp; validFor: RegExp }
  }
}

/**
 * Clear image cache (useful after adding new images)
 */
export function clearImageCache(documentId?: string) {
  if (documentId) {
    imageCache.delete(documentId)
  } else {
    imageCache.clear()
  }
}
